// The SmartSearch archive: NGF's old scanned files (2009 on), imported with their SmartSearch index values.
//   /archive              search (all NGF staff), the file shown beside the results
//   /archive/review       files not linked to a person, one person at a time, to link when convenient (technicians and admins);
//                         optional: they are found and opened on Search like any other file
//   /archive/departments  SmartSearch department text -> organization (admins)
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { ErrorBox } from '../components/Layout.tsx';
import { canImport, get, isAdmin, post } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { orgMatches } from '../lib/orgSearch.ts';
import { useData } from '../lib/useData.ts';

export interface ArchiveRow {
  id: string; docId: number; ext: string; name: string | null; dob: string | null; departmentText: string | null;
  departmentId: string | null; departmentName: string | null; year: number | null; label: string | null; indexedAt: string | null; indexedBulk: boolean; scannedOn: string | null; docDate: string | null;
  sizeBytes: number | null; hasFile: boolean; status: string; tier: number | null; reason: string | null; participantId: string | null;
  personFirst: string | null; personLast: string | null; personDob: string | null; filedAt: string | null; filedBy: string | null; skipReason: string | null;
}
interface Person {
  id: string; firstName: string; lastName: string; dob: string | null; sex?: string | null; employeeId: string | null; departmentName: string | null;
  lastVisit?: string | null; archiveFiles?: number; reason?: string; lastAssessedOn?: string | null;
}
interface Stats { total: number; filed: number; review: number; reviewPeople: number; skipped: number; withoutFile: number; departmentUnmapped: number; firstYear: number | null; lastYear: number | null }

export const STATUS_LABEL: Record<string, string> = {
  auto: 'Linked (exact match)', auto_close: 'Linked (close match)', filed: 'Linked by staff', created: 'Linked (new person)',
  review: 'Not linked to a person', skipped: 'Skipped', pending: 'Not matched yet',
};
const day = (s: string | null) => (s ? s.slice(0, 10) : '—');
const usDate = (iso: string | null) => (iso ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}/${iso.slice(0, 4)}` : '—');

function ArchiveTabs() {
  const { me } = useAuth();
  const stats = useData<Stats>('/api/archive/stats');
  const s = stats.data;
  return (
    <>
      <h1>SmartSearch archive</h1>
      <p className="muted" style={{ marginTop: 0 }}>NGF’s scanned files from the old SmartSearch program{s?.firstYear ? ` (${s.firstYear}–${s.lastYear})` : ''}, with
        the name, date of birth, department and year they were filed under.{s ? ` ${s.total.toLocaleString()} files; ${s.filed.toLocaleString()} linked to a person.` : ''}</p>
      <nav className="tabs no-print">
        <NavLink to="/archive" end>Search</NavLink>
        {me && canImport(me.role) && <NavLink to="/archive/review">Not linked</NavLink>}
        {me && isAdmin(me.role) && <NavLink to="/archive/departments">Departments</NavLink>}
      </nav>
    </>
  );
}

// ---------------------------------------------------------------- search

export function ArchiveSearch() {
  const [params, setParams] = useSearchParams();
  const [form, setForm] = useState(() => Object.fromEntries(['q', 'dept', 'yearFrom', 'yearTo', 'status', 'departmentId'].map((k) => [k, params.get(k) ?? ''])));
  const query = useMemo(() => {
    const p = new URLSearchParams();
    for (const k of ['q', 'dept', 'yearFrom', 'yearTo', 'status', 'departmentId', 'offset']) { const v = params.get(k); if (v) p.set(k, v); }
    return p.toString();
  }, [params]);
  const searching = ['q', 'dept', 'yearFrom', 'yearTo', 'departmentId'].some((k) => params.get(k)) || (params.get('status') ?? 'all') !== 'all';
  const { data, error, reload } = useData<{ total: number; rows: ArchiveRow[] }>(searching ? `/api/archive/search?${query}` : null);
  const selected = params.get('doc');
  const offset = Number(params.get('offset') ?? 0);
  const orgs = useData<{ id: string; name: string }[]>('/api/departments');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(form)) if (v) p.set(k, v);
    setParams(p);
  };
  const pick = (id: string) => { const p = new URLSearchParams(params); p.set('doc', id); setParams(p, { replace: true }); };
  const page = (o: number) => { const p = new URLSearchParams(params); p.set('offset', String(o)); p.delete('doc'); setParams(p); };

  return (
    <>
      <ArchiveTabs />
      <form className="card ev-filters" onSubmit={submit}>
        <input aria-label="Name or date of birth" placeholder="Name (Smith John) or date of birth (03/07/1988)" value={form.q} onChange={(e) => setForm({ ...form, q: e.target.value })} autoFocus />
        <select aria-label="Organization" value={form.departmentId} onChange={(e) => setForm({ ...form, departmentId: e.target.value })}>
          <option value="">Any organization</option>
          {(orgs.data ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <input aria-label="Department as typed" placeholder="Department as typed in SmartSearch" value={form.dept} onChange={(e) => setForm({ ...form, dept: e.target.value })} style={{ flex: '0 1 220px' }} />
        <span className="ev-date">Year <input aria-label="From year" type="number" min={1990} max={2100} value={form.yearFrom} onChange={(e) => setForm({ ...form, yearFrom: e.target.value })} style={{ width: '6em' }} />
          to <input aria-label="To year" type="number" min={1990} max={2100} value={form.yearTo} onChange={(e) => setForm({ ...form, yearTo: e.target.value })} style={{ width: '6em' }} /></span>
        <select aria-label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
          <option value="">Linked or not</option><option value="filed">Linked to a person</option><option value="review">Not linked to a person</option><option value="skipped">Skipped</option>
        </select>
        <button type="submit">Search</button>
      </form>
      <ErrorBox error={error} />
      {!searching ? <p className="muted">Search by name, date of birth, organization, department or year.</p> : !data ? <p className="muted">Searching…</p> : (
        <div className={selected ? 'import-review' : ''}>
          <div className="card">
            <p className="muted small" style={{ marginTop: 0 }}>{data.total.toLocaleString()} file{data.total === 1 ? '' : 's'}{data.total > data.rows.length ? `, showing ${offset + 1}–${offset + data.rows.length}` : ''}</p>
            {!data.rows.length ? <p className="muted">No files match.</p> : (
              <div className="table-scroll"><table>
                <thead><tr><th>Name in SmartSearch</th><th>DOB</th><th>Department</th><th>Year</th><th>Filed with</th></tr></thead>
                <tbody>{data.rows.map((r) => (
                  <tr key={r.id} className={`link${selected === r.id ? ' selected' : ''}`} onClick={() => pick(r.id)}>
                    <td>{r.name ?? <span className="muted">no name</span>}{r.label && <div className="muted small">{r.label}</div>}</td>
                    <td className="nowrap">{usDate(r.dob)}</td>
                    <td>{r.departmentText ?? '—'}{r.departmentName && r.departmentName.toLowerCase() !== (r.departmentText ?? '').toLowerCase() && <div className="muted small">{r.departmentName}</div>}</td>
                    <td>{r.year ?? '—'}</td>
                    <td>{r.participantId ? <Link to={`/participants/${r.participantId}`} onClick={(e) => e.stopPropagation()}>{r.personLast}, {r.personFirst}</Link>
                      : <span className={`badge archive-${r.status}`}>{STATUS_LABEL[r.status]}</span>}</td>
                  </tr>))}</tbody>
              </table></div>)}
            {data.total > data.rows.length && (
              <div className="spread" style={{ marginTop: '0.5rem' }}>
                <button className="secondary" disabled={offset === 0} onClick={() => page(Math.max(0, offset - 100))}>‹ Previous</button>
                <button className="secondary" disabled={offset + data.rows.length >= data.total} onClick={() => page(offset + 100)}>Next ›</button>
              </div>)}
          </div>
          {selected && <ArchiveViewer id={selected} onChanged={() => void reload()} />}
        </div>)}
    </>
  );
}

/** One archive file: the PDF and its SmartSearch index values; corrections for importers. */
export function ArchiveViewer({ id, onChanged }: { id: string; onChanged?: () => void }) {
  const { me } = useAuth();
  const { data: d, error, reload } = useData<ArchiveRow & { raw: Record<string, unknown> }>(`/api/archive/${id}`);
  const [moving, setMoving] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  useEffect(() => { setMoving(false); setErr(null); }, [id]);
  if (error) return <ErrorBox error={error} />;
  if (!d) return <p className="muted">Loading…</p>;
  const act = async (fn: () => Promise<unknown>) => { setErr(null); try { await fn(); setMoving(false); await reload(); onChanged?.(); } catch (x) { setErr(x); } };
  const unfile = () => { const reason = prompt('Why unlink this file from the person?'); if (reason) void act(() => post(`/api/archive/${id}/unfile`, { reason })); };
  return (
    <div className="import-scan archive-viewer">
      {d.hasFile && d.ext === 'pdf' ? <iframe src={`/api/archive/${id}/file#view=FitH&toolbar=1`} title={`SmartSearch file ${d.docId}`} />
        : d.hasFile ? <div className="card"><p>This file is a TIFF image, which most browsers cannot show. <a href={`/api/archive/${id}/file?download=1`}>Download it</a>.</p></div>
        : <div className="card"><p className="muted">This file has not been copied into the app yet. Its index values are below.</p></div>}
      <div className="card">
        {d.hasFile && <p className="small" style={{ marginTop: 0 }}><a href={`/api/archive/${id}/file`} target="_blank" rel="noreferrer">Open in a new tab</a> · <a href={`/api/archive/${id}/file?download=1`}>Download</a></p>}
        <dl className="archive-fields">
          <dt>Name</dt><dd>{d.name ?? '—'}</dd>
          <dt>Date of birth</dt><dd>{usDate(d.dob)}</dd>
          <dt>Department</dt><dd>{d.departmentText ?? '—'}{d.departmentName && <span className="muted"> → {d.departmentName}</span>}</dd>
          <dt>Year</dt><dd>{d.year ?? '—'}</dd>
          {d.label && <><dt>Label</dt><dd>{d.label}</dd></>}
          <dt>Date</dt><dd>{d.docDate ? usDate(d.docDate) : <>{d.year ?? '—'} <span className="muted small">(year only)</span></>}{d.scannedOn && <span className="muted small"> (scanned)</span>}</dd>
          <dt>Filed in SmartSearch</dt><dd>{day(d.indexedAt)}{d.indexedBulk && <span className="muted small"> (moved in with many others from the earlier system: not the date of the file)</span>} <span className="muted small">(SmartSearch # {d.docId})</span></dd>
          <dt>In this app</dt><dd>{d.participantId
            ? <>Linked to <Link to={`/participants/${d.participantId}`}>{d.personLast}, {d.personFirst}</Link> (DOB {usDate(d.personDob)}) · <span className="muted">{STATUS_LABEL[d.status]}{d.filedBy ? ` by ${d.filedBy}` : ''}{d.filedAt ? `, ${day(d.filedAt)}` : ''}</span></>
            : <>{STATUS_LABEL[d.status]}{d.skipReason ? `: ${d.skipReason}` : ''}{d.reason && d.status === 'review' ? <span className="muted"> ({d.reason})</span> : ''}</>}</dd>
        </dl>
        <ErrorBox error={err} />
        {me && canImport(me.role) && (
          <div className="no-print">
            {d.status === 'review' ? <button className="secondary small" onClick={() => setMoving(!moving)}>Link to a person</button> : <>
              <button className="secondary small" onClick={() => setMoving(!moving)}>{d.participantId ? 'Move to another person' : 'Link to a person'}</button>{' '}
              {d.participantId && <button className="secondary small" onClick={unfile}>Unlink</button>}
            </>}
            {moving && <PersonFinder initial={d.name ?? ''} onPick={(p) => {
              const reason = prompt(`Why link it to ${p.lastName}, ${p.firstName}?`);
              if (reason) void act(() => post(`/api/archive/${id}/move`, { participantId: p.id, reason }));
            }} />}
          </div>)}
      </div>
    </div>
  );
}

/** Search people on file (name, ID # or date of birth) and pick one. */
function PersonFinder({ initial, onPick }: { initial: string; onPick: (p: Person) => void }) {
  const [q, setQ] = useState(initial.replace(',', ''));
  const [hits, setHits] = useState<Person[] | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const find = async (e?: FormEvent) => {
    e?.preventDefault(); setErr(null);
    if (!q.trim()) return;
    try {
      // The participant search matches "Last, First" or "First Last" as typed: try both orders for "Smith John".
      const parts = q.trim().split(/\s+/);
      const tries = [q.trim(), parts.length === 2 ? `${parts[0]}, ${parts[1]}` : null, parts.length === 2 ? `${parts[1]} ${parts[0]}` : null].filter(Boolean) as string[];
      const all = new Map<string, Person>();
      for (const t of tries) for (const p of await get<Person[]>(`/api/participants?q=${encodeURIComponent(t)}&limit=20`)) all.set(p.id, p);
      setHits([...all.values()]);
    } catch (x) { setErr(x); }
  };
  return (
    <div className="person-finder">
      <form onSubmit={find} className="row"><input placeholder="Name, ID # or date of birth" value={q} onChange={(e) => setQ(e.target.value)} /><button className="secondary small" type="submit">Find</button></form>
      <ErrorBox error={err} />
      {hits && (!hits.length ? <p className="muted small">No one on file matches.</p> : (
        <ul className="plain">{hits.map((p) => (
          <li key={p.id}><button type="button" className="link" onClick={() => onPick(p)}>{p.lastName}, {p.firstName}</button>
            <span className="muted small"> · DOB {usDate(p.dob)} · {p.departmentName ?? 'Unassigned'}{p.employeeId ? ` · ID ${p.employeeId}` : ''}{p.lastAssessedOn ? ` · last visit ${day(p.lastAssessedOn)}` : ''}</span></li>))}</ul>))}
    </div>
  );
}

// ---------------------------------------------------------------- review

interface Group { nameKey: string; docs: number; ids: string[]; name: string | null; dob: string | null; tier: number; reason: string; departments: string[]; years: number[]; candidates: Person[] }

const TIER_LABEL: Record<number, string> = {
  1: 'More than one person on file has this exact name and date of birth',
  2: 'More than one person on file is close',
  3: 'Partly matches someone on file',
  4: 'No one on file matches',
  5: 'No date of birth in SmartSearch',
};

export function ArchiveReview() {
  const [q, setQ] = useState('');
  const [tier, setTier] = useState('');
  const [offset, setOffset] = useState(0);
  const [applied, setApplied] = useState({ q: '', tier: '' });
  const { data, error, reload } = useData<{ total: number; groups: Group[] }>(
    `/api/archive/review?limit=10&offset=${offset}${applied.q ? `&q=${encodeURIComponent(applied.q)}` : ''}${applied.tier ? `&tier=${applied.tier}` : ''}`);
  return (
    <>
      <ArchiveTabs />
      <p className="muted" style={{ marginTop: 0 }}>Files that are not linked to a person record, usually because the person was never in ePhysioLogic or has
        two records. <b>Nothing here has to be done:</b> these files are found and opened on <Link to="/archive">Search</Link> like any other. Link them when
        it helps, one card per name and date of birth: to a suggested person, someone else, or a new person.</p>
      <form className="card ev-filters" onSubmit={(e) => { e.preventDefault(); setOffset(0); setApplied({ q, tier }); }}>
        <input placeholder="Name or department" value={q} onChange={(e) => setQ(e.target.value)} />
        <select aria-label="Why" value={tier} onChange={(e) => setTier(e.target.value)}>
          <option value="">All reasons</option>{[1, 2, 3, 4, 5].map((t) => <option key={t} value={t}>{TIER_LABEL[t]}</option>)}
        </select>
        <button type="submit">Show</button>
      </form>
      <ErrorBox error={error} />
      {!data ? <p className="muted">Loading…</p> : !data.groups.length ? <div className="card"><p className="muted">No unlinked files{applied.q || applied.tier ? ' match' : ''}.</p></div> : <>
        <p className="muted small">{data.total.toLocaleString()} people with unlinked files, showing {offset + 1}–{offset + data.groups.length}.</p>
        {data.groups.map((g) => <ReviewGroup key={g.nameKey} g={g} onDone={() => void reload()} />)}
        <div className="spread">
          <button className="secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 10))}>‹ Previous</button>
          <button className="secondary" disabled={offset + data.groups.length >= data.total} onClick={() => setOffset(offset + 10)}>Next ›</button>
        </div>
      </>}
    </>
  );
}

/** Split a SmartSearch name ("Last, First M" or "First Last") for the new-person form. */
function splitName(n: string | null) {
  const s = (n ?? '').trim();
  if (s.includes(',')) { const [l = '', f = ''] = s.split(',', 2); return { first: f.trim().split(/\s+/)[0] ?? '', last: l.trim() }; }
  const w = s.split(/\s+/);
  return w.length > 1 ? { first: w[0]!, last: w.slice(1).join(' ') } : { first: '', last: s };
}

function ReviewGroup({ g, onDone }: { g: Group; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [doc, setDoc] = useState(g.ids[0]!);
  const [mode, setMode] = useState<'' | 'find' | 'create'>('');
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const n = splitName(g.name);
  const [np, setNp] = useState({ firstName: n.first, lastName: n.last, dob: g.dob ?? '', sex: '' });
  const act = async (fn: () => Promise<unknown>) => { setErr(null); setBusy(true); try { await fn(); onDone(); } catch (x) { setErr(x); } finally { setBusy(false); } };
  const fileWith = (p: Person) => act(() => post('/api/archive/review/file', { ids: g.ids, participantId: p.id }));
  return (
    <div className="card archive-group">
      <div className="spread">
        <div>
          <strong>{g.name ?? 'No name'}</strong> · DOB {usDate(g.dob)} · {g.docs} file{g.docs === 1 ? '' : 's'}
          <div className="muted small">{TIER_LABEL[g.tier] ?? g.reason} · {g.departments.slice(0, 3).join(', ') || 'no department'}{g.departments.length > 3 ? ` +${g.departments.length - 3}` : ''}
            {g.years.length > 0 && ` · ${Math.min(...g.years)}${g.years.length > 1 ? `–${Math.max(...g.years)}` : ''}`}</div>
        </div>
        <button className="secondary small" onClick={() => setOpen(!open)}>{open ? 'Hide files' : 'See files'}</button>
      </div>
      {open && (
        <div className="archive-group-files">
          {g.ids.length > 1 && <div className="chips">{g.ids.map((id, i) => <button key={id} type="button" className={`chip${doc === id ? ' active' : ''}`} onClick={() => setDoc(id)}>File {i + 1}</button>)}</div>}
          <ArchiveViewer id={doc} />
        </div>)}
      {g.candidates.length > 0 && (
        <div className="archive-candidates">
          <div className="muted small">Suggested:</div>
          {g.candidates.map((p) => (
            <div key={p.id} className="row">
              <button className="small" disabled={busy} onClick={() => void fileWith(p)}>File with</button>
              <span><Link to={`/participants/${p.id}`} target="_blank">{p.lastName}, {p.firstName}</Link>
                <span className="muted small"> · DOB {usDate(p.dob)} · {p.departmentName ?? 'Unassigned'}{p.lastVisit ? ` · last visit ${p.lastVisit}` : ''}{p.archiveFiles ? ` · ${p.archiveFiles} archive files` : ''} · {p.reason}</span></span>
            </div>))}
        </div>)}
      <div className="row" style={{ marginTop: '0.5rem' }}>
        <button className="secondary small" onClick={() => setMode(mode === 'find' ? '' : 'find')}>Find someone else</button>
        <button className="secondary small" onClick={() => setMode(mode === 'create' ? '' : 'create')}>Create the person</button>
        <button className="secondary small" disabled={busy} onClick={() => { const reason = prompt('Why skip these files? (they stay searchable)'); if (reason) void act(() => post('/api/archive/review/skip', { ids: g.ids, reason })); }}>Skip</button>
      </div>
      {mode === 'find' && <PersonFinder initial={g.name ?? ''} onPick={(p) => void fileWith(p)} />}
      {mode === 'create' && (
        <form className="archive-create" onSubmit={(e) => { e.preventDefault(); void act(() => post('/api/archive/review/create', { ids: g.ids, firstName: np.firstName, lastName: np.lastName, dob: np.dob || null, sex: np.sex || null })); }}>
          <input aria-label="First name" placeholder="First name" value={np.firstName} onChange={(e) => setNp({ ...np, firstName: e.target.value })} required />
          <input aria-label="Last name" placeholder="Last name" value={np.lastName} onChange={(e) => setNp({ ...np, lastName: e.target.value })} required />
          <input aria-label="Date of birth" type="date" value={np.dob} onChange={(e) => setNp({ ...np, dob: e.target.value })} />
          <select aria-label="Sex" value={np.sex} onChange={(e) => setNp({ ...np, sex: e.target.value })}><option value="">Sex?</option><option value="M">M</option><option value="F">F</option></select>
          <button className="small" type="submit" disabled={busy}>Create and file {g.docs} file{g.docs === 1 ? '' : 's'}</button>
          <span className="muted small">Organization: the one the department is mapped to.</span>
        </form>)}
      <ErrorBox error={err} />
    </div>
  );
}

// ---------------------------------------------------------------- departments (admins)

interface DeptRow { deptKey: string; docs: number; example: string; spellings: string[]; departmentId: string | null; departmentName: string | null; source: string | null }

export function ArchiveDepartments() {
  const [show, setShow] = useState<'unmapped' | 'mapped' | 'all'>('unmapped');
  const [q, setQ] = useState('');
  const [applied, setApplied] = useState('');
  const [offset, setOffset] = useState(0);
  const { data, error, reload } = useData<{ total: number; rows: DeptRow[] }>(
    `/api/archive/departments?show=${show}&limit=50&offset=${offset}${applied ? `&q=${encodeURIComponent(applied)}` : ''}`);
  const orgs = useData<{ id: string; name: string }[]>('/api/departments');
  const nav = useNavigate();
  const SOURCE: Record<string, string> = { people: 'from the people filed', name: 'same name', admin: 'set by NGF' };
  return (
    <>
      <ArchiveTabs />
      <p className="muted" style={{ marginTop: 0 }}>The department typed in SmartSearch, and the organization it means. Used to search by organization and for
        people created from the archive; it never moves anyone between organizations. Most files first.</p>
      <form className="card ev-filters" onSubmit={(e) => { e.preventDefault(); setOffset(0); setApplied(q); }}>
        <input placeholder="Department or organization" value={q} onChange={(e) => setQ(e.target.value)} />
        <select aria-label="Show" value={show} onChange={(e) => { setOffset(0); setShow(e.target.value as typeof show); }}>
          <option value="unmapped">Not mapped yet</option><option value="mapped">Mapped</option><option value="all">All</option>
        </select>
        <button type="submit">Show</button>
      </form>
      <ErrorBox error={error} />
      {!data ? <p className="muted">Loading…</p> : (
        <div className="card">
          <p className="muted small" style={{ marginTop: 0 }}>{data.total.toLocaleString()} department spellings.</p>
          <div className="table-scroll"><table>
            <thead><tr><th>As typed</th><th>Files</th><th>Organization</th><th></th></tr></thead>
            <tbody>{data.rows.map((r) => (
              <tr key={r.deptKey}>
                <td>{r.example}{r.spellings.length > 1 && <div className="muted small">also: {r.spellings.filter((s) => s !== r.example).slice(0, 4).join(' · ')}</div>}</td>
                <td><button type="button" className="link" onClick={() => nav(`/archive?dept=${encodeURIComponent(r.example)}`)}>{r.docs.toLocaleString()}</button></td>
                <td>{r.departmentName ?? (r.source ? <span className="muted">no organization</span> : '—')}{r.source && <div className="muted small">{SOURCE[r.source]}</div>}</td>
                <td><OrgChoice orgs={orgs.data ?? []} onPick={async (id) => { await post('/api/archive/departments', { deptKey: r.deptKey, departmentId: id }); await reload(); }} /></td>
              </tr>))}</tbody>
          </table></div>
          {data.total > data.rows.length && (
            <div className="spread" style={{ marginTop: '0.5rem' }}>
              <button className="secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 50))}>‹ Previous</button>
              <button className="secondary" disabled={offset + data.rows.length >= data.total} onClick={() => setOffset(offset + 50)}>Next ›</button>
            </div>)}
        </div>)}
    </>
  );
}

/** Type to find an organization; pick it (or "No organization"). */
function OrgChoice({ orgs, onPick }: { orgs: { id: string; name: string }[]; onPick: (id: string | null) => Promise<void> }) {
  const [q, setQ] = useState('');
  const [err, setErr] = useState<unknown>(null);
  const hits = q.trim().length >= 2 ? orgs.filter((o) => orgMatches(o.name, q)).slice(0, 6) : [];
  const choose = async (id: string | null) => { setErr(null); try { await onPick(id); setQ(''); } catch (x) { setErr(x); } };
  return (
    <div className="org-picker">
      <input type="search" placeholder="Type an organization…" value={q} onChange={(e) => setQ(e.target.value)} />
      {q.trim().length >= 2 && <ul className="org-picker-hits">
        {hits.map((o) => <li key={o.id}><button type="button" onClick={() => void choose(o.id)}>{o.name}</button></li>)}
        <li><button type="button" className="muted" onClick={() => void choose(null)}>No organization</button></li>
      </ul>}
      <ErrorBox error={err} />
    </div>
  );
}
